import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ObjectActionMenu } from "@/object-actions/renderers/ObjectActionMenu";
import { ObjectActionMenuCloseProvider } from "@/object-actions/renderers/ObjectActionMenuCloseContext";
import type { ObjectKind, ResolvedObjectAction } from "@/object-actions/types";
import { MoreHorizontal } from "lucide-react";
import { useCallback, type ReactNode } from "react";

export interface ObjectActionPaneHeaderKebabMenuProps {
  actions: ResolvedObjectAction[];
  kind: ObjectKind;
  name?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Extra items after registry actions (e.g. export format submenu). */
  children?: ReactNode;
}

/**
 * Trailing "more" menu for pane headers. Renders the same `menu-item` rows as
 * left-rail / main-view dropdowns (`ObjectActionMenu`), driven by the
 * `left-rail` surface manifest at the call site.
 */
export function ObjectActionPaneHeaderKebabMenu({
  actions,
  kind,
  name,
  open,
  onOpenChange,
  children,
}: ObjectActionPaneHeaderKebabMenuProps) {
  const label = name ?? "Item";
  const closeMenu = useCallback(() => onOpenChange(false), [onOpenChange]);

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`${label} actions`}
          onClick={(e) => e.stopPropagation()}
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4} className="w-44">
        <ObjectActionMenuCloseProvider value={closeMenu}>
          <ObjectActionMenu actions={actions} kind={kind} name={name}>
            {children}
          </ObjectActionMenu>
        </ObjectActionMenuCloseProvider>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
